// Active brand store + cockpit theming.
//
// When the operator switches brands, we live-swap --accent + --accent-soft
// CSS variables so the entire chrome (sidebar accent, topbar button, focus
// rings, badges) re-tints to the brand's primary color. Falls back to the
// Nexus red when no brand is selected.
//
// This implements design/Handoff.html §03 — "Brand cockpit theming —
// live CSS variable swap".

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Brand } from "@/lib/types";

const NEXUS_RED = "#E02D1C";
const NEXUS_RED_SOFT = "rgba(224,45,28,0.12)";

interface ActiveBrandState {
  activeBrandId: string | null;
  /** Reduced version of the active brand — kept in store so theming
   *  doesn't need to re-fetch on every page. */
  activeBrand: Brand | null;
  setActive: (brand: Brand | null) => void;
}

export const useActiveBrand = create<ActiveBrandState>()(
  persist(
    (set) => ({
      activeBrandId: null,
      activeBrand: null,
      setActive: (brand) => {
        applyAccent(brand);
        set({
          activeBrandId: brand?.id ?? null,
          activeBrand: brand,
        });
      },
    }),
    {
      name: "nexus.active-brand",
      partialize: (s) => ({ activeBrandId: s.activeBrandId }),
    },
  ),
);

/** Apply the brand's primary accent to --accent + --accent-soft. */
function applyAccent(brand: Brand | null) {
  const root = document.documentElement;
  const accent = brand?.accent_hex || NEXUS_RED;
  const soft = brand?.accent_soft_hex || hexToSoft(accent) || NEXUS_RED_SOFT;
  root.style.setProperty("--accent", accent);
  root.style.setProperty("--accent-soft", soft);
}

function hexToSoft(hex: string | undefined): string | null {
  if (!hex || hex.length < 4) return null;
  const h = hex.replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r + g + b)) return null;
  return `rgba(${r},${g},${b},0.12)`;
}
